<template>
  <!-- Asks before launching a support session (utils/support_session) into
       the organization App.vue opened it for, and which of its apps when it
       has both. A launch the API refuses (the organization is disabled, or
       lost the app) is answered here too. -->
  <confirm-modal
    :title="`Launch a session in ${name}?`"
    subtitle="Opens the app here, in a desktop window, signed in as its admin."
    icon="pi-desktop"
    confirm-label="Launch session"
    :busy="isLoading"
    @confirm="launch"
    @close="close"
  >
    <div v-if="apps.length > 1" class="ls-choice" role="radiogroup" aria-labelledby="ls-choice-label">
      <p id="ls-choice-label" class="ls-choice__label">Which app?</p>
      <div class="ls-choice__options">
        <button
          v-for="app in apps"
          :key="app.value"
          type="button"
          role="radio"
          class="ls-option"
          :class="{ 'is-selected': chosen === app.value }"
          :aria-checked="chosen === app.value ? 'true' : 'false'"
          :disabled="isLoading"
          @click="chosen = app.value"
        >
          <span class="ls-mark" aria-hidden="true"><img :src="MARKS[app.value]" alt="" /></span>
          <span class="ls-option__text">
            <span class="ls-option__name">{{ app.name }}</span>
            <span class="ls-option__host">{{ app.host }}</span>
          </span>
          <span class="ls-option__radio" aria-hidden="true"></span>
        </button>
      </div>
    </div>

    <div v-else-if="apps.length === 1" class="ls-single">
      <span class="ls-mark" aria-hidden="true"><img :src="MARKS[apps[0].value]" alt="" /></span>
      <span class="ls-single__text">
        <span class="ls-single__name">Opens {{ apps[0].name }}</span>
        <span class="ls-single__host">{{ apps[0].host }}</span>
      </span>
    </div>

    <p v-if="error" class="ls-error" role="alert">
      <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
      <span>{{ error }}</span>
    </p>
  </confirm-modal>
</template>
<script>
import confirmModal from '@/components/Confirm_Modal.vue';
import chabadDigitalMark from '@/assets/images/brands/chabad-digital-icon.png';
import screenkodeMark from '@/assets/images/logo-icon.png';
import { CLOSE_SUPPORT_SESSION_LAUNCHER, START_SUPPORT_SESSION } from '@/store/action_types';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { organizationApps } from '@/utils/organization_app';

const MARKS = { screenkode: screenkodeMark, chabad_digital: chabadDigitalMark };

export default {
  name: 'LaunchSessionModal',
  components: {
    confirmModal,
  },
  data() {
    return {
      MARKS,
      chosen: null,
      error: '',
    };
  },
  computed: {
    organization() {
      return this.$store.getters.supportSessionLauncher;
    },
    isLoading() {
      return this.$store.getters.supportSessionIsLoading;
    },
    name() {
      return this.organization?.name || 'this organization';
    },
    // The apps its people can sign in to — the session opens one of them.
    apps() {
      return organizationApps(this.organization);
    },
    chosenApp() {
      return this.apps.find((app) => app.value === this.chosen) || null;
    },
  },
  watch: {
    apps: {
      immediate: true,
      handler(apps) {
        if (!apps.some((app) => app.value === this.chosen)) {
          this.chosen = apps.length ? apps[0].value : null;
        }
      },
    },
  },
  methods: {
    close() {
      if (!this.isLoading) this.$store.dispatch(CLOSE_SUPPORT_SESSION_LAUNCHER);
    },
    async launch() {
      if (this.isLoading || !this.organization) return;
      if (!this.chosenApp) {
        this.error = `${this.name} has no app to open. Give it access to one in its Settings first.`;
        return;
      }
      if (!this.organization.isActive) {
        this.error = `${this.name} is disabled. Switch it on in its Settings before launching a session.`;
        return;
      }
      this.error = '';
      const app = this.chosenApp;
      const result = await this.$store.dispatch(START_SUPPORT_SESSION, {
        organization: this.organization,
        app: app.value,
      });
      if (result && result.ok) {
        // The store closed this dialog; the desktop is opening.
        this.$toast.success(`Session started in ${app.name}.`, TOAST_GLOBAL_CONFIG);
        return;
      }
      this.error = (result && result.message) || 'The session could not be started. Try again.';
    },
  },
};
</script>
<style scoped>
.ls-choice__label {
  margin: 0 0 8px;
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
}

.ls-choice__options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 170px), 1fr));
  gap: 10px;
}

/* A radio card: the app's mark, name and address, and a dot once chosen. */
.ls-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 11px 12px;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-lg);
  background: var(--color-bg-canvas);
  text-align: left;
  cursor: pointer;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
}

.ls-option:hover:not(:disabled) {
  border-color: var(--color-primary-border) !important;
  background: var(--color-bg-soft);
}

.ls-option.is-selected {
  border-color: var(--color-primary) !important;
  background: var(--color-primary-soft);
  box-shadow: 0 0 0 1px var(--color-primary);
}

.ls-option:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus) !important;
}

.ls-option:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.ls-mark {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
}

.ls-mark img {
  width: auto;
  height: 22px;
}

.ls-option__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.ls-option__name {
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 700;
}

.ls-option__host {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: 0.74rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ls-option__radio {
  flex: none;
  width: 18px;
  height: 18px;
  border: 2px solid var(--color-border-strong);
  border-radius: 50%;
  background: var(--color-bg-canvas);
  transition: border-color .15s ease, box-shadow .15s ease;
}

.ls-option.is-selected .ls-option__radio {
  border-color: var(--color-primary);
  box-shadow: inset 0 0 0 4px var(--color-primary);
}

/* One app: said, not asked. */
.ls-single {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-soft);
}

.ls-single__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ls-single__name {
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 700;
}

.ls-single__host {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: 0.74rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ls-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 14px 0 0;
  padding: 10px 12px;
  border: 1px solid #F9C9C9;
  border-radius: var(--radius-md);
  background: #FEECEC;
  color: #C81E1E;
  font-size: 0.8rem;
  line-height: 1.5;
}

.ls-error .pi {
  flex: none;
  margin-top: 2px;
  font-size: 0.85rem;
}
</style>
